// src/hooks/useSound.ts - COMPLETE SOUND MANAGEMENT HOOK WITH FIXED AUDIO
import { useState, useEffect, useRef, useCallback } from 'react';
import { SOUNDS, FALLBACK_SOUNDS, playFallbackBeep, getSoundType } from '../constants/notifications';

interface UseSoundOptions {
  defaultVolume?: number;
  soundEnabled?: boolean;
  onPlay?: () => void;
  onError?: (error: Error) => void;
}

interface SoundCache {
  [key: string]: HTMLAudioElement;
}

export const useSound = (options: UseSoundOptions = {}) => {
  const {
    defaultVolume = 0.7,
    soundEnabled: initialEnabled = true,
    onPlay,
    onError
  } = options;

  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('sound-enabled');
    return saved !== null ? JSON.parse(saved) : initialEnabled;
  });

  const [volume, setVolume] = useState<number>(() => {
    const saved = localStorage.getItem('sound-volume');
    return saved !== null ? JSON.parse(saved) : defaultVolume;
  });

  const [audioUnlocked, setAudioUnlocked] = useState<boolean>(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const soundCacheRef = useRef<SoundCache>({});
  const audioElementsRef = useRef<HTMLAudioElement[]>([]);
  const unlockAttemptedRef = useRef<boolean>(false);

  // Pre-load all sounds
  useEffect(() => {
    // Pre-load all sound files
    Object.entries(SOUNDS).forEach(([key, url]) => {
      if (!soundCacheRef.current[key]) {
        const audio = new Audio();
        audio.src = url;
        audio.preload = 'auto';
        audio.load();
        soundCacheRef.current[key] = audio;
        audioElementsRef.current.push(audio);
      }
    });

    // Also pre-load fallback sounds
    Object.entries(FALLBACK_SOUNDS).forEach(([key, url]) => {
      const audio = new Audio();
      audio.src = url;
      audio.preload = 'auto';
      audio.load();
      audioElementsRef.current.push(audio);
    });

    // Cleanup
    return () => {
      audioElementsRef.current.forEach(audio => {
        audio.pause();
        audio.src = '';
      });
      audioElementsRef.current = [];
      soundCacheRef.current = {};
      
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Initialize and unlock audio on first user interaction
  useEffect(() => {
    if (unlockAttemptedRef.current) return;

    const unlockAudio = async () => {
      if (unlockAttemptedRef.current) return;
      unlockAttemptedRef.current = true;

      try {
        // Method 1: Web Audio API
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          audioContextRef.current = new AudioContextClass();
          
          if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
          }
          
          // Play a silent sound to unlock
          const oscillator = audioContextRef.current.createOscillator();
          const gainNode = audioContextRef.current.createGain();
          gainNode.gain.value = 0.01; // Almost silent
          oscillator.connect(gainNode);
          gainNode.connect(audioContextRef.current.destination);
          oscillator.start();
          oscillator.stop(audioContextRef.current.currentTime + 0.01);
          
          setAudioUnlocked(true);
          console.log('🔊 Audio unlocked via Web Audio API');
        }

        // Method 2: HTML5 Audio - create a test audio
        const testAudio = new Audio();
        testAudio.volume = 0.01;
        // Use a local path that exists in public folder
        testAudio.src = '/sounds/notification.mp3';
        await testAudio.play().catch(() => {
          // Try fallback sound
          testAudio.src = 'https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3';
          return testAudio.play();
        }).catch(() => {
          // Ignore errors
        });
        testAudio.pause();
        
        setAudioUnlocked(true);
        console.log('🔊 Audio unlocked via HTML5 Audio');

      } catch (error) {
        console.log('⚠️ Audio unlock attempt failed:', error);
        // Still mark as unlocked to prevent repeated attempts
        setAudioUnlocked(true);
      }

      // Remove all event listeners after first interaction
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };

    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
    document.addEventListener('keydown', unlockAudio);

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // Enhanced play function with multiple fallback methods
  const playSound = useCallback(async (soundType: string) => {
    if (!soundEnabled) {
      console.log('🔇 Sound disabled');
      return false;
    }

    if (!audioUnlocked) {
      console.log('🔇 Audio not yet unlocked - waiting for user interaction');
      // Try to unlock again
      try {
        const testAudio = new Audio();
        testAudio.volume = 0.01;
        testAudio.src = '/sounds/notification.mp3';
        await testAudio.play().catch(() => {});
        setAudioUnlocked(true);
      } catch (error) {
        // Ignore
      }
      return false;
    }

    console.log(`🔊 Attempting to play sound: ${soundType}`);

    // Map notification types to sound keys
    const soundKey = soundType in SOUNDS ? soundType : 'system';

    // Method 1: Try to play from public folder first
    try {
      const audio = new Audio(`/sounds/${soundKey}.mp3`);
      audio.volume = volume;
      await audio.play();
      console.log(`✅ Played from public folder: ${soundKey}.mp3`);
      onPlay?.();
      return true;
    } catch (error) {
      console.log(`⚠️ Public folder sound failed: ${error}`);
    }

    // Method 2: Cached HTML5 Audio
    if (soundCacheRef.current[soundKey]) {
      try {
        const audio = soundCacheRef.current[soundKey];
        audio.volume = volume;
        audio.currentTime = 0;
        await audio.play();
        console.log(`✅ HTML5 Audio played: ${soundKey}`);
        onPlay?.();
        return true;
      } catch (error) {
        console.log(`⚠️ HTML5 Audio failed: ${error}`);
      }
    }

    // Method 3: Create new Audio element
    try {
      const audio = new Audio(SOUNDS[soundKey] || SOUNDS.system);
      audio.volume = volume;
      await audio.play();
      console.log(`✅ New Audio element played: ${soundKey}`);
      onPlay?.();
      return true;
    } catch (error) {
      console.log(`⚠️ New Audio element failed: ${error}`);
    }

    // Method 4: Web Audio API beep
    try {
      const soundTypeForBeep = getSoundType(soundType);
      playFallbackBeep(soundTypeForBeep);
      console.log(`✅ Web Audio beep played: ${soundTypeForBeep}`);
      onPlay?.();
      return true;
    } catch (error) {
      console.log(`⚠️ Web Audio beep failed: ${error}`);
    }

    // Method 5: Vibration (mobile)
    try {
      if (navigator.vibrate) {
        navigator.vibrate(200);
        console.log('✅ Vibration used');
        onPlay?.();
        return true;
      }
    } catch (error) {
      console.log(`⚠️ Vibration failed: ${error}`);
    }

    // All methods failed
    console.error('❌ All sound playback methods failed');
    onError?.(new Error('All sound playback methods failed'));
    return false;
  }, [soundEnabled, audioUnlocked, volume, onPlay, onError]);

  // Toggle sound enabled/disabled
  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('sound-enabled', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  // Change volume
  const changeVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    localStorage.setItem('sound-volume', JSON.stringify(clampedVolume));
    
    // Update all cached audio elements volume
    Object.values(soundCacheRef.current).forEach(audio => {
      audio.volume = clampedVolume;
    });
  }, []);

  // Test sound function
  const testSound = useCallback(() => {
    return playSound('system');
  }, [playSound]);

  return {
    soundEnabled,
    volume,
    audioUnlocked,
    playSound,
    toggleSound,
    changeVolume,
    testSound,
  };
};

export default useSound;